/**
 * SPATIAL_FILTER (AcDbSpatialFilter / AcDbFilter) OBJECTS-section parser.
 *
 * EPIC-VIEWPORT-CLIP SLICE 2 — `S-OBJECTS-SPATIAL-FILTER` (the actual EPIC
 * blocker: there is ZERO producer for XCLIP / SPATIAL_FILTER today). This
 * module is the OBJECTS-section PRODUCER — it decodes the SPATIAL_FILTER
 * record that backs an XCLIP / VPCLIP boundary into a typed
 * `DxfSpatialFilter`. The handle-join resolver that maps the filter back
 * to its owning VIEWPORT / block reference and transforms the boundary to
 * WCS is SLICE 3 (`resolveViewportClip`, handleResolution.ts) — NOT here.
 *
 * Mirrors `objects/objectsAnnotativeParser.ts` (the AcDbScale producer):
 * `isSpatialFilterType` is the dispatch predicate (mirror of
 * `isAcDbScaleType`); `parseSpatialFilterEntry` is the record decoder
 * (mirror of `parseAcDbScale`). The writer sibling is
 * `objects/spatialFilterWriter.ts`. Like AcDbScale, SPATIAL_FILTER is
 * pulled OUT of `rawObjectGroups` — the writer reconstructs the record
 * from the typed list (see `parseObjectsSection` + `serializeObjectsSection`).
 *
 * ## Group-code grammar (verified — ezdxf `spatial_filter.py` + Autodesk
 * DXF reference SPATIAL_FILTER, both agree):
 *
 * ```
 * 100 AcDbFilter                   ← base subclass marker
 * 100 AcDbSpatialFilter            ← concrete subclass marker
 * 70  <boundary point count>
 * 10/20  <clip boundary point>     ← repeats <count> times, 2D
 * 210/220/230 <extrusion>          ← plane normal (default 0,0,1)
 * 11/21/31    <origin>             ← clip-boundary local CS origin (default 0,0,0)
 * 71  <clip boundary display flag>
 * 72  <has front clipping plane>
 * 40  <front clip distance>        ← ONLY when 72 == 1
 * 73  <has back clipping plane>
 * 41  <back clip distance>         ← ONLY when 73 == 1
 * 40 x12  <inverse_insert_matrix>  ← 4x3 column-major
 * 40 x12  <transform_matrix>       ← 4x3 column-major
 * ```
 *
 * ## The code-40 overload (the one round-trip gotcha)
 *
 * Group 40 is used by THREE distinct fields: the optional front-clip
 * distance and the two trailing 12-real matrices. We disambiguate exactly
 * as ezdxf does — positionally: collect every code-40 real, take the LAST
 * 24 as the two matrices (inverse = [-24:-12], transform = [-12:]); any
 * earlier code-40 real is the front-clip distance (present iff flag 72=1).
 * Group 41 (back-clip distance) is distinct and unambiguous.
 *
 * Decode policy: field-TOLERANT, lossless. We decode the full known
 * grammar; any group code outside it (vendor / future-AutoCAD) is carried
 * verbatim in `rawGroups` so the round-trip never drops a byte (CLAUDE.md
 * priority #1). The two matrices are preserved verbatim even though only
 * the inverse matrix is consumed downstream (SLICE 3).
 *
 * ## ABSOLUTE SOURCE-ORDINAL preservation model
 *
 * The parser walks the source `groups` array with an ABSOLUTE index. Every
 * group is classified TYPED (decoded into a field the writer re-emits) or RAW
 * (preserved verbatim). For each TYPED group it records the source ordinal in
 * `typedOrds` IN THE EXACT ORDER THE WRITER EMITS its typed groups; for each
 * RAW group it records `{ ord, group }` (the ORIGINAL `DxfGroup` by reference,
 * never a rebuild). The writer zips its canonical typed-group list with
 * `typedOrds`, then MERGES typed-(ord,group) + raw-(ord,group) by ascending
 * ord. Because every group re-emits at its true source position, a raw group at
 * ANY sub-position round-trips byte-identically by construction. This supersedes
 * the old coarse named-"slot" anchor model (Codex #319 P2 — 7 positional
 * findings).
 */

import { advanceControlDepth } from "../controlBlockDepth";
import type { DxfGroup } from "../types";
import { parseFloatSafe, parseIntSafe } from "./groupCodec";
import type {
  DxfSpatialFilter,
  SpatialFilterPoint,
  SpatialFilter3DPoint,
  SpatialFilterOrdinalRawGroup,
  SpatialFilterPresence,
} from "./spatialFilterTypes";

export type {
  DxfSpatialFilter,
  SpatialFilterPoint,
  SpatialFilter3DPoint,
  SpatialFilterClipPlane,
  SpatialFilterOrdinalRawGroup,
  SpatialFilterPresence,
} from "./spatialFilterTypes";
export { SPATIAL_FILTER_PLACEHOLDER_CODE } from "./spatialFilterTypes";

// ── Subclass markers ──────────────────────────────────────────────────

const SUBCLASS_FILTER = "AcDbFilter";
const SUBCLASS_SPATIAL_FILTER = "AcDbSpatialFilter";

/** Number of reals in one 4×3 column-major transform matrix. */
const MATRIX_REALS = 12;
/** Both matrices together — the trailing code-40 block. */
const MATRIX_PAIR_REALS = MATRIX_REALS * 2;

// ── isSpatialFilterType ────────────────────────────────────────────────

/**
 * Identify whether a parsed object record is a SPATIAL_FILTER. Examines
 * both the record type (group 0) and the 100-subclass marker chain — the
 * marker chain is authoritative (mirrors `isAcDbScaleType`): some writers
 * emit a vendor-extended group-0 type but always carry the canonical
 * `AcDbSpatialFilter` subclass marker.
 */
export function isSpatialFilterType(
  type: string,
  groups: DxfGroup[],
): boolean {
  if (type === "SPATIAL_FILTER") return true;
  for (const g of groups) {
    if (g.code === 100 && g.value.trim() === SUBCLASS_SPATIAL_FILTER) {
      return true;
    }
  }
  return false;
}

// ── parseSpatialFilterEntry ────────────────────────────────────────────

/**
 * One TYPED group recorded during the walk: the canonical-order slot it
 * occupies plus its ABSOLUTE source ordinal. After the walk these are sorted
 * by `slot` (canonical writer order) and projected to `typedOrds` so the
 * writer can zip each emitted typed group with its source ord. `slot` is an
 * integer position in the canonical emit sequence — see `TypedSlot`.
 */
type TypedOrdEntry = { slot: number; ord: number };

/**
 * Canonical writer emit order, expressed as ascending `slot` integers. The
 * parser stamps each typed group with its slot; the writer emits in this exact
 * order, so sorting `TypedOrdEntry[]` by `slot` yields `typedOrds` aligned to
 * the writer's canonical typed-group sequence. The matrix slots and the
 * per-group malformed-boundary / boundary-point groups carry sub-indices via a
 * fractional offset so multiple groups under one logical field keep source
 * order among themselves.
 */
const TypedSlot = {
  objectType: 0,
  handle: 1,
  owner: 2,
  subclassFilter: 3,
  subclass: 4,
  // 5.x — the boundary block (synthesised 70 + points, OR per-group malformed
  // groups). Sub-index keeps the block's groups in source order.
  boundaryBase: 5,
  extrusion: 6, // 6.0/6.1/6.2 — 210/220/230
  origin: 7, // 7.0/7.1/7.2 — 11/21/31
  clipActive: 8,
  frontFlag: 9,
  frontDist: 10,
  backFlag: 11,
  backDist: 12,
  // 13.x — inverse matrix ×12; 14.x — forward matrix ×12.
  inverseBase: 13,
  forwardBase: 14,
} as const;

/**
 * Decode a single SPATIAL_FILTER record from its raw group list (group 0
 * header included). Inverse of `serializeSpatialFilter`.
 *
 * @param groups full group list of the record (0/5/330/100.../70/10.../40...).
 */
export function parseSpatialFilterEntry(groups: DxfGroup[]): DxfSpatialFilter {
  let handle = "";
  let handleOrd: number | undefined; // source ordinal of the group-5 handle
  let ownerHandle = "";
  let ownerOrd: number | undefined; // source ordinal of the top-level group-330
  // The ORIGINAL group-0 record type as it appeared on disk. A vendor record
  // may carry a non-canonical group-0 name while still presenting the
  // `AcDbSpatialFilter` subclass marker (which is what `isSpatialFilterType`
  // dispatched on). Captured verbatim so the writer re-emits the exact name
  // instead of canonicalising to "SPATIAL_FILTER" (Codex #319 P2, finding 3).
  let objectType: string | undefined;
  let objectTypeOrd: number | undefined; // source ordinal of the group-0 header
  // Source ordinals of the two canonical subclass markers (100 AcDbFilter /
  // 100 AcDbSpatialFilter) the writer reconstructs.
  let subclassFilterOrd: number | undefined;
  let subclassOrd: number | undefined;

  // Boundary polygon — accumulated from repeating 10/20 pairs. For a
  // well-formed block `boundaryPoints` is authoritative and the writer
  // re-derives the 70 count. For a MALFORMED block (declared 70 ≠ actual
  // pair count, or an orphan ordinate) we must NOT canonicalise the count
  // (preserve-source-never-infer): the raw block (70 + every 10/20 in
  // source order, incl. orphans) is carried verbatim in `boundaryRaw` and
  // re-emitted by the writer. We collect both forms during the walk, then
  // decide which is authoritative after.
  const boundaryPoints: SpatialFilterPoint[] = [];
  let pendingBoundaryX: DxfGroup | undefined; // the unmatched 10 group, if any
  // Verbatim boundary-block groups in source order — the declared 70 count
  // group plus every 10/20 ordinate group (incl. orphans), each with its
  // absolute source ordinal. Used only when the block proves malformed.
  const boundaryRaw: { ord: number; group: DxfGroup }[] = [];
  let declaredCount: number | undefined; // DXF 70 as declared on disk
  let seventyCount = 0; // number of DXF 70 groups seen (cardinality check)
  let orphanOrdinate = false; // a 10 without its 20, or a 20 without its 10

  // Optional 3D fields — captured verbatim only when present on disk so a
  // record that omitted them (relying on the AutoCAD default) round-trips
  // without a synthesised tag. The triplet's source ordinals are remembered so
  // the writer can re-emit each axis at its true position.
  let extrusion: SpatialFilter3DPoint | undefined;
  let exX: number | undefined;
  let exY: number | undefined;
  let exZ: number | undefined;
  let exOrdX: number | undefined;
  let exOrdY: number | undefined;
  let exOrdZ: number | undefined;
  let origin: SpatialFilter3DPoint | undefined;
  let orX: number | undefined;
  let orY: number | undefined;
  let orZ: number | undefined;
  let orOrdX: number | undefined;
  let orOrdY: number | undefined;
  let orOrdZ: number | undefined;

  let clipActive = true; // DXF 71 default = 1 (ezdxf is_clipping_enabled)
  let clipActiveOrd: number | undefined; // ordinal of the 71 group IFF present
  let frontEnabled = false; // DXF 72 default = 0
  let frontFlagOrd: number | undefined;
  let backEnabled = false; // DXF 73 default = 0
  let backFlagOrd: number | undefined;

  // Every code-40 real in document order, paired with its source ordinal AND
  // its ORIGINAL group object (load-bearing for the raw-40-spelling case: a
  // disabled leading 40 must re-emit its EXACT source string, not a
  // formatGroupNumber rebuild). The trailing 24 are the two matrices; an
  // earlier one (at most one, present iff flag 72) is the front-clip distance.
  // Disambiguated after the walk (mirrors ezdxf).
  const matrix40: { value: number; ord: number; group: DxfGroup }[] = [];

  // Code-41 candidates in document order, each with its source ordinal. After
  // the walk the FIRST is promoted to the typed `backClip.distance` IFF flag
  // 73 == 1; every other (and ALL when 73 == 0) stays a RAW group at its ord so
  // the value round-trips (Codex #319 P2, finding 2).
  const clip41: { value: number; ord: number; group: DxfGroup }[] = [];

  // Unknown body groups — preserved verbatim at their ABSOLUTE source ordinal
  // (preserve-source-never-infer). The writer merges these with the typed
  // groups by ord, so each re-emits at its EXACT source sub-position.
  const rawOrdinal: SpatialFilterOrdinalRawGroup[] = [];
  const pushRaw = (ord: number, g: DxfGroup): void => {
    // CLONE the group (not by reference) so the preserved raw stream cannot be
    // aliased / mutated by a downstream consumer holding the original parsed
    // group (Codex pre-PR — raw-by-reference). `value` is the verbatim source
    // string, so the raw-40 spelling ("4.5000"/"1.0E+1") is still preserved
    // exactly. `rawGroups` derives from this list, so it inherits the clone.
    rawOrdinal.push({ ord, group: { code: g.code, value: g.value } });
  };

  // `102` control-block nesting depth. A SPATIAL_FILTER (like any DXF object)
  // can carry APP-group blocks — `102 {ACAD_REACTORS … 330 <reactor> … 102 }`
  // and/or `102 {ACAD_XDICTIONARY 360 <handle> … 102 }` — BEFORE its real owner
  // soft-pointer (a top-level `330` outside any `102` block). The `330` INSIDE
  // such a block is a reactor cross-link, NOT the owner; taking the first `330`
  // unconditionally would mis-assign the reactor handle as `ownerHandle` and
  // SLICE-3's resolveViewportClip would join on the wrong handle. We track the
  // open/close depth via the shared `advanceControlDepth` helper (same logic
  // `parseDynamicBlockObject` uses) and only read `330` as the owner at depth 0.
  // Every group inside an open block — the `102` markers and the nested
  // 330/360/etc. handles — is preserved verbatim in `rawGroups` so the writer
  // re-emits the block byte-for-byte (preserve-source-never-infer).
  let controlDepth = 0;

  for (let ord = 0; ord < groups.length; ord++) {
    const g = groups[ord]!;
    const nextDepth = advanceControlDepth(g, controlDepth);
    if (nextDepth !== undefined) {
      // A `102` open/close marker — never a record-level field. Update depth
      // and carry the marker verbatim at its ordinal so the writer re-emits the
      // whole block in its original position.
      controlDepth = nextDepth;
      pushRaw(ord, g);
      continue;
    }
    if (controlDepth > 0) {
      // Inside an open `102` block — the inner 330 (reactor) / 360 (xdict) and
      // any other code is part of the nested structure, NOT a record field.
      pushRaw(ord, g);
      continue;
    }
    switch (g.code) {
      case 0:
        // Record header. Capture the ORIGINAL type string verbatim (a vendor
        // record may use a non-canonical group-0 name yet carry the
        // AcDbSpatialFilter subclass marker — Codex #319 P2, finding 3). The
        // writer re-emits this exact value, NOT a hardcoded "SPATIAL_FILTER".
        // A DUPLICATE group-0 (should not happen) is preserved raw.
        if (objectType === undefined) {
          objectType = g.value;
          objectTypeOrd = ord;
        } else pushRaw(ord, g);
        break;
      case 5:
        if (handle === "") {
          handle = g.value.trim();
          handleOrd = ord;
        } else pushRaw(ord, g);
        break;
      case 330:
        if (ownerHandle === "") {
          ownerHandle = g.value.trim();
          ownerOrd = ord;
        } else pushRaw(ord, g);
        break;
      case 100: {
        const marker = g.value.trim();
        // Both canonical subclass markers are reconstructed by the writer.
        if (marker === SUBCLASS_FILTER && subclassFilterOrd === undefined) {
          subclassFilterOrd = ord;
          break;
        }
        if (
          marker === SUBCLASS_SPATIAL_FILTER &&
          subclassOrd === undefined
        ) {
          subclassOrd = ord;
          break;
        }
        pushRaw(ord, g);
        break;
      }
      case 70:
        // Boundary point count. Retained verbatim (boundaryRaw) + parsed
        // (declaredCount) so a malformed count is preserved, not silently
        // re-derived from boundaryPoints.length. `seventyCount` tracks the 70
        // CARDINALITY: a well-formed block has EXACTLY one 70. Zero 70s (the
        // writer must not INVENT one) or multiple 70s (both must be preserved)
        // make the block malformed → raw-carry path (see `isMalformed`).
        seventyCount += 1;
        declaredCount = parseIntSafe(g.value);
        boundaryRaw.push({ ord, group: g });
        break;
      case 10:
        if (pendingBoundaryX !== undefined) {
          // Two 10s in a row ⇒ the previous 10 had no 20: orphan ordinate.
          orphanOrdinate = true;
        }
        pendingBoundaryX = g;
        boundaryRaw.push({ ord, group: g });
        break;
      case 20:
        boundaryRaw.push({ ord, group: g });
        if (pendingBoundaryX !== undefined) {
          boundaryPoints.push({
            x: parseFloatSafe(pendingBoundaryX.value),
            y: parseFloatSafe(g.value),
          });
          pendingBoundaryX = undefined;
        } else {
          // A 20 with no preceding 10 ⇒ orphan ordinate.
          orphanOrdinate = true;
        }
        break;
      // Extrusion / origin axes — each tag is the typed value ONLY on its FIRST
      // occurrence (mirror of the 71/72/73 guards below). A DUPLICATE axis tag
      // (a second 210, …) does NOT overwrite the typed value/ord — that would
      // silently drop the first occurrence (its ord is never recorded nor
      // pushed, so it vanishes on serialize). Instead route the duplicate to
      // `pushRaw` so it re-emits verbatim at its source position (the ordinal
      // merge handles raw; preserve-source-never-infer — CLAUDE.md priority #1).
      case 210:
        if (exOrdX === undefined) {
          exX = parseFloatSafe(g.value);
          exOrdX = ord;
        } else pushRaw(ord, g);
        break;
      case 220:
        if (exOrdY === undefined) {
          exY = parseFloatSafe(g.value);
          exOrdY = ord;
        } else pushRaw(ord, g);
        break;
      case 230:
        if (exOrdZ === undefined) {
          exZ = parseFloatSafe(g.value);
          exOrdZ = ord;
        } else pushRaw(ord, g);
        break;
      case 11:
        if (orOrdX === undefined) {
          orX = parseFloatSafe(g.value);
          orOrdX = ord;
        } else pushRaw(ord, g);
        break;
      case 21:
        if (orOrdY === undefined) {
          orY = parseFloatSafe(g.value);
          orOrdY = ord;
        } else pushRaw(ord, g);
        break;
      case 31:
        if (orOrdZ === undefined) {
          orZ = parseFloatSafe(g.value);
          orOrdZ = ord;
        } else pushRaw(ord, g);
        break;
      case 71:
        // Only the FIRST 71 is the typed flag; a duplicate is raw.
        if (clipActiveOrd === undefined) {
          clipActive = parseIntSafe(g.value) !== 0;
          clipActiveOrd = ord;
        } else pushRaw(ord, g);
        break;
      case 72:
        if (frontFlagOrd === undefined) {
          frontEnabled = parseIntSafe(g.value) !== 0;
          frontFlagOrd = ord;
        } else pushRaw(ord, g);
        break;
      case 73:
        if (backFlagOrd === undefined) {
          backEnabled = parseIntSafe(g.value) !== 0;
          backFlagOrd = ord;
        } else pushRaw(ord, g);
        break;
      case 40:
        matrix40.push({ value: parseFloatSafe(g.value), ord, group: g });
        // Note: a code-40 real may be the front-clip distance, a defensive
        // leading real, or part of a matrix block — disambiguated AFTER the
        // walk, where the matrix reals + the (optional) front distance become
        // typed and every other leading 40 becomes a RAW group carrying its
        // ORIGINAL source group (raw-40 spelling, Codex #319 P2 finding 6).
        break;
      case 41:
        // Back-clip distance candidate. Captured VERBATIM with its ordinal so
        // a 41 present while 73 == 0, and any duplicate 41, survive the
        // round-trip (Codex #319 P2, finding 2). The typed-vs-raw split happens
        // after the walk once flag 73 is known (73 may appear before OR after
        // the 41).
        clip41.push({ value: parseFloatSafe(g.value), ord, group: g });
        break;
      default:
        pushRaw(ord, g);
        break;
    }
  }

  if (exX !== undefined || exY !== undefined || exZ !== undefined) {
    // Documented default extrusion (plane normal) is the Z axis (0,0,1). A
    // PARTIAL triplet (e.g. only 210 present) fills the absent axes with that
    // default VALUE for downstream consumers — but the writer re-emits ONLY the
    // axes whose source ordinal exists (`presence.extrusion*`), so an omitted
    // 230 never gains a synthesised `230 0` (Codex #319 P2, finding 4 — omitted
    // 230 default = 1, not 0).
    extrusion = { x: exX ?? 0, y: exY ?? 0, z: exZ ?? 1 };
  }
  if (orX !== undefined || orY !== undefined || orZ !== undefined) {
    // Documented default origin is (0,0,0). Same per-axis presence rule applies.
    origin = { x: orX ?? 0, y: orY ?? 0, z: orZ ?? 0 };
  }

  // A trailing 10 with no following 20 is itself an orphan ordinate.
  if (pendingBoundaryX !== undefined) orphanOrdinate = true;
  // Boundary block is MALFORMED when:
  //   • the declared 70 count disagrees with the number of decoded 10/20 pairs,
  //   • an orphan ordinate exists, OR
  //   • the 70 CARDINALITY is not exactly 1 while a boundary block IS present
  //     (zero 70 with ordinates — the writer would otherwise INVENT a 70 the
  //     source lacked; or multiple 70s — the writer would otherwise drop the
  //     duplicates via the synthesise-on-write path).
  // preserve-source-never-infer: do NOT canonicalise — carry the raw block
  // verbatim so the writer re-emits the exact source bytes (every 70 + every
  // ordinate, in source order). This mirrors the existing declared≠pair /
  // orphan-ordinate raw-carry already in the module.
  const isMalformed =
    (declaredCount !== undefined && declaredCount !== boundaryPoints.length) ||
    orphanOrdinate ||
    // 70-cardinality != 1 is malformed → verbatim raw-carry (no synthesized 70).
    // Covers BOTH a present-but-wrong-count boundary AND an ENTIRELY MISSING
    // boundary block (no 70 + no 10/20 ⇒ boundaryRaw empty ⇒ seventyCount 0):
    // an empty `malformedBoundary` ([]) then routes the writer to its
    // malformed branch, which emits NOTHING for the boundary (preserving the
    // source's absence) instead of synthesizing a `70 0` (Codex #323 P2).
    seventyCount !== 1;
  const malformedBoundary = isMalformed
    ? // CLONE (not by reference) so the preserved malformed-boundary stream
      // cannot be aliased / mutated downstream (Codex pre-PR — raw-by-reference).
      boundaryRaw.map((b) => ({ code: b.group.code, value: b.group.value }))
    : undefined;

  // Disambiguate the overloaded code-40 stream (mirrors ezdxf's
  // matrix_values[-24:-12] / [-12:] split): the LAST 24 reals are the two
  // matrices; an earlier real is the front-clip distance. `matrixStart` is the
  // index in `matrix40` where the matrix pair begins (== leading-real count).
  let inverseInsertMatrix: number[];
  let transformMatrix: number[];
  let frontDistance = 0;
  let frontDistOrd: number | undefined;
  const inverseOrds: number[] = [];
  const forwardOrds: number[] = [];
  const haveMatrixPair = matrix40.length >= MATRIX_PAIR_REALS;
  const matrixStart = haveMatrixPair
    ? matrix40.length - MATRIX_PAIR_REALS
    : matrix40.length; // degenerate: NO matrix pair — every 40 is leading
  if (haveMatrixPair) {
    const inv = matrix40.slice(matrixStart, matrixStart + MATRIX_REALS);
    const fwd = matrix40.slice(matrixStart + MATRIX_REALS);
    inverseInsertMatrix = inv.map((m) => m.value);
    transformMatrix = fwd.map((m) => m.value);
    for (const m of inv) inverseOrds.push(m.ord);
    for (const m of fwd) forwardOrds.push(m.ord);
  } else {
    // Degenerate record without the full matrix pair — preserve whatever
    // code-40 reals exist verbatim and emit empty matrices so downstream
    // (SLICE 3) treats the record as untransformable rather than crashing.
    inverseInsertMatrix = [];
    transformMatrix = [];
  }
  // The reals BEFORE the matrix pair: when the front-clip flag is set the LAST
  // leading real is the front-clip distance (typed, at its source ord); every
  // OTHER leading real becomes a RAW group carrying its ORIGINAL source group
  // (so its exact source string round-trips — Codex #319 P2, finding 6).
  const leading = matrix40.slice(0, matrixStart);
  const frontDistIdx =
    frontEnabled && leading.length > 0 ? leading.length - 1 : -1;
  if (frontDistIdx >= 0) {
    frontDistance = leading[frontDistIdx]!.value;
    frontDistOrd = leading[frontDistIdx]!.ord;
  }
  for (let i = 0; i < leading.length; i++) {
    if (i === frontDistIdx) continue; // consumed as the typed front distance
    // Carry the ORIGINAL source group by reference (NOT a formatGroupNumber
    // rebuild) so a "4.5000" / "1.0E+1" spelling survives byte-identically.
    pushRaw(leading[i]!.ord, leading[i]!.group);
  }

  // Resolve back-clip (41) candidates: the FIRST is promoted to the typed
  // distance IFF flag 73 == 1; every other (and ALL when 73 == 0) stays a RAW
  // group at its ord (Codex #319 P2, finding 2).
  let backDistance = 0;
  let backDistOrd: number | undefined;
  let backTaken = false;
  for (const c of clip41) {
    if (backEnabled && !backTaken) {
      backDistance = c.value;
      backDistOrd = c.ord;
      backTaken = true;
      continue;
    }
    pushRaw(c.ord, c.group);
  }

  // ── Assemble typed-group ordinals in canonical writer order ────────────
  // Each typed group the writer emits gets a `{ slot, ord }`; sorting by slot
  // (ascending, matching the writer's fixed sequence) and projecting `ord`
  // yields `typedOrds` aligned to the writer's typed-group stream.
  const typedEntries: TypedOrdEntry[] = [];
  const addTyped = (slot: number, ord: number | undefined): void => {
    if (ord !== undefined) typedEntries.push({ slot, ord });
  };
  addTyped(TypedSlot.objectType, objectTypeOrd);
  addTyped(TypedSlot.handle, handle ? handleOrd : undefined);
  addTyped(TypedSlot.owner, ownerHandle ? ownerOrd : undefined);
  addTyped(TypedSlot.subclassFilter, subclassFilterOrd);
  addTyped(TypedSlot.subclass, subclassOrd);
  // Boundary block. The writer emits one group per typed-boundary ord in source
  // order: MALFORMED ⇒ each original 70/10/20 group verbatim; WELL-FORMED ⇒ a
  // synthesised 70 + each 10/20. The typed-ords are the source ordinals of the
  // block's groups so it re-emits at its true position AND an interleaved raw
  // inside the block (between a 10 and its 20) slots correctly between them.
  //
  // A WELL-FORMED block ALWAYS has EXACTLY one source 70 (the `isMalformed`
  // cardinality guard routes zero-70 and multiple-70 blocks to the verbatim
  // raw-carry path instead — so the writer never INVENTS a 70 the source lacked
  // nor drops a duplicate). `boundaryRaw` for a well-formed block therefore IS
  // `[the 70, then every 10/20]`, and its ords map 1:1 to the writer's
  // synthesised 70 + points — no synthetic-ord adjustment needed.
  const boundaryOrds: number[] = boundaryRaw.map((b) => b.ord);
  for (let i = 0; i < boundaryOrds.length; i++) {
    typedEntries.push({
      slot: TypedSlot.boundaryBase + i / (boundaryOrds.length + 1),
      ord: boundaryOrds[i]!,
    });
  }
  addTyped(TypedSlot.extrusion + 0.0, exOrdX);
  addTyped(TypedSlot.extrusion + 0.1, exOrdY);
  addTyped(TypedSlot.extrusion + 0.2, exOrdZ);
  addTyped(TypedSlot.origin + 0.0, orOrdX);
  addTyped(TypedSlot.origin + 0.1, orOrdY);
  addTyped(TypedSlot.origin + 0.2, orOrdZ);
  addTyped(TypedSlot.clipActive, clipActiveOrd);
  addTyped(TypedSlot.frontFlag, frontFlagOrd);
  addTyped(TypedSlot.frontDist, frontDistOrd);
  addTyped(TypedSlot.backFlag, backFlagOrd);
  addTyped(TypedSlot.backDist, backDistOrd);
  for (let i = 0; i < inverseOrds.length; i++) {
    typedEntries.push({
      slot: TypedSlot.inverseBase + i / (inverseOrds.length + 1),
      ord: inverseOrds[i]!,
    });
  }
  for (let i = 0; i < forwardOrds.length; i++) {
    typedEntries.push({
      slot: TypedSlot.forwardBase + i / (forwardOrds.length + 1),
      ord: forwardOrds[i]!,
    });
  }
  typedEntries.sort((a, b) => a.slot - b.slot);
  const typedOrds = typedEntries.map((e) => e.ord);

  // Flat raw-group projection (ascending ord) for downstream / back-compat.
  rawOrdinal.sort((a, b) => a.ord - b.ord);
  const rawGroups = rawOrdinal.map((r) => r.group);

  const filter: DxfSpatialFilter = {
    handle,
    boundaryPoints,
    clipActive,
    frontClip: { enabled: frontEnabled, distance: frontDistance },
    backClip: { enabled: backEnabled, distance: backDistance },
    inverseInsertMatrix,
    transformMatrix,
  };
  // Preserve a non-canonical group-0 name verbatim (Codex #319 P2, finding 3).
  // Omit when it is exactly the canonical "SPATIAL_FILTER" (the writer default)
  // so well-formed records do not gain a redundant field.
  if (objectType !== undefined && objectType !== "SPATIAL_FILTER") {
    filter.objectType = objectType;
  }
  if (ownerHandle) filter.ownerHandle = ownerHandle;
  if (extrusion) filter.extrusion = extrusion;
  if (origin) filter.origin = origin;
  // Track whether the source carried a 71 so the writer does not synthesise one
  // for a 71-omitted record (Codex #319 P2, omitted-71).
  if (clipActiveOrd !== undefined) filter.clipActivePresent = true;
  // When the boundary block is malformed, `boundaryPoints` is non-authoritative
  // (the verbatim block governs the writer); keep the parsed points too so
  // downstream readers that ignore the malformed flag still see the pairs
  // that did decode, but the writer prefers `malformedBoundary`.
  if (malformedBoundary) filter.malformedBoundary = malformedBoundary;
  // Always emit the ordinal companion on a PARSED record so the writer merges
  // typed + raw by source position (byte-identical round-trip). `typedOrds` is
  // present even with zero raw groups (the merge is then a canonical-order
  // no-op for well-formed input).
  filter.typedOrds = typedOrds;
  // Per-typed-group SOURCE PRESENCE — the writer emits each typed group ONLY
  // when its source ordinal exists (Codex #319 P2 — present-iff-ordinal). Each
  // flag MIRRORS the corresponding `addTyped(...)` gate above EXACTLY, so the
  // writer's present-filtered typed list aligns 1:1 with `typedOrds`. Defaulted
  // VALUES (extrusion Z = 1, …) stay in the model for slice-3 consumers; only
  // EMISSION is gated here.
  const presence: SpatialFilterPresence = {
    subclassFilter: subclassFilterOrd !== undefined,
    subclass: subclassOrd !== undefined,
    extrusionX: exOrdX !== undefined,
    extrusionY: exOrdY !== undefined,
    extrusionZ: exOrdZ !== undefined,
    originX: orOrdX !== undefined,
    originY: orOrdY !== undefined,
    originZ: orOrdZ !== undefined,
    frontFlag: frontFlagOrd !== undefined,
    frontDist: frontDistOrd !== undefined,
    backFlag: backFlagOrd !== undefined,
    backDist: backDistOrd !== undefined,
  };
  filter.presence = presence;
  if (rawGroups.length > 0) {
    filter.rawGroups = rawGroups;
    filter.rawGroupsOrdinal = rawOrdinal;
  }
  return filter;
}
